import { lazy, Suspense } from "react";
import { useAuth } from "@/hooks/useAuth";

/*
 * The gate. Nothing inside the dashboard renders until there is a session AND
 * a registered passkey — the mandate is enforced here, in one place, rather
 * than per-route where a new route could silently miss it.
 *
 * The signed-out branch is the only thing that changed when the public site
 * moved into this app: it used to render the unlock screen directly, and now
 * renders a small public router (the marketing landing at "/", /about, and the
 * unlock screen at /unlock). The gate itself is untouched — "signed out" still
 * means "cannot see a single byte of the dashboard", and passkey enrolment is
 * still mandatory.
 *
 * Every branch is lazily loaded: a returning user with a live session
 * downloads none of the public site, and a first-time visitor arriving from an
 * ad downloads none of the dashboard.
 */
const PublicApp = lazy(() => import("@/marketing/PublicApp"));
const EnrolPasskey = lazy(() => import("@/pages/EnrolPasskey"));
const ApiUnavailable = lazy(() => import("@/pages/ApiUnavailable"));

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();

  /* Deliberately blank rather than a spinner: this resolves in one request,
     and a spinner that flashes for 80ms reads as jank, not as progress. */
  if (status === "checking") return null;

  if (status === "signed-out") {
    return <Suspense fallback={null}><PublicApp /></Suspense>;
  }

  if (status === "enrolling") {
    return <Suspense fallback={null}><EnrolPasskey /></Suspense>;
  }

  if (status === "unavailable") {
    return <Suspense fallback={null}><ApiUnavailable /></Suspense>;
  }

  return <>{children}</>;
}
